import { Chip, Skeleton, Typography, TextField, InputAdornment, Button } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useEffect, useMemo, useState, memo, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import PlaceholderImg from "../../assets/images/placeholder_img.jpg";
import { Postrequestcall } from "../../request/Postrequest";
import { GET_NEARBY_PLACES } from "../../constant/Apipath";

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

/**
 * apiValue = what gets sent to the backend as `type`
 * aliases  = what the user might type in the search box
 */
const KNOWN_CATEGORY_MAP = {
  cafe:               { label: "Cafés",              icon: "☕", apiValue: "cafe",            aliases: ["cafe", "coffee", "cafes"] },
  convenience_store:  { label: "Convenience Stores", icon: "🏪", apiValue: "convenience_store", aliases: ["convenience", "convenience-store", "convenience store"] },
  restaurant:         { label: "Restaurants",        icon: "🍽️", apiValue: "restaurant",       aliases: ["restaurant", "food", "dining", "eat"] },
  gas_station:        { label: "Gas Stations",       icon: "⛽", apiValue: "gas_station",       aliases: ["gas", "petrol", "fuel", "gas-station", "gas station"] },
  hardware_store:     { label: "Hardware Stores",    icon: "🔧", apiValue: "hardware_store",    aliases: ["hardware", "hardware-store", "tools"] },
  supermarket:        { label: "Grocery",            icon: "🛒", apiValue: "supermarket",       aliases: ["grocery", "groceries", "supermarket", "market"] },
  hospital:           { label: "Hospitals",          icon: "🏥", apiValue: "hospital",          aliases: ["hospital", "emergency"] },
  pharmacy:           { label: "Pharmacy",           icon: "💊", apiValue: "pharmacy",          aliases: ["pharmacy", "chemist", "medicine"] },
  bank:               { label: "Banks",              icon: "🏦", apiValue: "bank",              aliases: ["bank", "banking"] },
  atm:                { label: "ATMs",               icon: "🏧", apiValue: "atm",               aliases: ["atm", "cash"] },
  school:             { label: "Schools",            icon: "🏫", apiValue: "school",            aliases: ["school"] },
  university:         { label: "Universities",       icon: "🎓", apiValue: "university",        aliases: ["university", "college", "education"] },
  police:             { label: "Police Stations",    icon: "🚔", apiValue: "police",            aliases: ["police", "station"] },
  lodging:            { label: "Hotels & Lodging",   icon: "🏨", apiValue: "lodging",           aliases: ["hotel", "lodging", "stay", "accommodation"] },
  shopping_mall:      { label: "Shopping Malls",     icon: "🛍️", apiValue: "shopping_mall",     aliases: ["mall", "shopping"] },
  health:             { label: "Health & Clinics",   icon: "🩺", apiValue: "health",            aliases: ["health", "clinic", "medical"] },
  doctor:             { label: "Doctors",            icon: "👨‍⚕️", apiValue: "doctor",            aliases: ["doctor", "physician"] },
  lawyer:             { label: "Legal Services",     icon: "⚖️", apiValue: "lawyer",            aliases: ["lawyer", "legal", "attorney"] },
  accounting:         { label: "Accounting",         icon: "📊", apiValue: "accounting",        aliases: ["accounting", "accountant"] },
  insurance_agency:   { label: "Insurance",          icon: "🛡️", apiValue: "insurance_agency",  aliases: ["insurance"] },
  real_estate_agency: { label: "Real Estate",        icon: "🏠", apiValue: "real_estate_agency",aliases: ["real estate", "property", "realty"] },
  electronics_store:  { label: "Electronics",        icon: "💻", apiValue: "electronics_store", aliases: ["electronics", "computer", "tech"] },
  furniture_store:    { label: "Furniture",          icon: "🛋️", apiValue: "furniture_store",   aliases: ["furniture"] },
  store:              { label: "Stores",             icon: "🏬", apiValue: "store",             aliases: ["store", "shop"] },
  general_contractor: { label: "Contractors",        icon: "🏗️", apiValue: "general_contractor",aliases: ["contractor", "builder"] },
  campground:         { label: "Campgrounds",        icon: "⛺", apiValue: "campground",        aliases: ["camp", "camping"] },
  hindu_temple:       { label: "Temples",            icon: "🛕", apiValue: "hindu_temple",      aliases: ["temple", "mandir", "worship"] },
  // public restroom uses a keyword, not a type — backend receives "public restroom"
  public_restroom:    { label: "Public Washrooms",   icon: "🚻", apiValue: "public restroom",   aliases: ["washroom", "toilet", "restroom", "bathroom", "public-washrooms", "public washrooms", "wc"] },
  point_of_interest:  { label: "Points of Interest", icon: "📍", apiValue: "point_of_interest", aliases: ["point of interest", "poi", "landmark"] },
};

// ─── Utilities ────────────────────────────────────────────────────────────────

const toTitleCase = (v) =>
  v.split(/[-_\s]+/).map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join(" ");

const toRadians = (v) => (v * Math.PI) / 180;

const calcDistanceMeters = (lat1, lon1, lat2, lon2) => {
  const R = 6371000;
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const buildMapsUrl = (placeId, name, lat, lng) => {
  if (placeId) return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(name || "")}&query_place_id=${placeId}`;
  if (lat && lng) return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  if (name) return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(name)}`;
  return "";
};

/** Returns the apiValue to send to the backend (e.g. "public restroom", "supermarket") */
const resolveSearchToApiValue = (text) => {
  const q = text.trim().toLowerCase();
  if (!q) return "all";
  // Exact match first
  for (const def of Object.values(KNOWN_CATEGORY_MAP)) {
    if (def.label.toLowerCase() === q || def.aliases.some((a) => a === q)) return def.apiValue;
  }
  // Partial match
  for (const def of Object.values(KNOWN_CATEGORY_MAP)) {
    if (def.label.toLowerCase().includes(q) || def.aliases.some((a) => a.includes(q) || q.includes(a))) return def.apiValue;
  }
  return q; // unknown — pass raw to backend
};

// ─── Normalisation ────────────────────────────────────────────────────────────

const normalizePlace = (place, siteLat, siteLng) => {
  const placeLat = Number(place.position?.latitude ?? place.latitude ?? place.lat ?? 0);
  const placeLng = Number(place.position?.longitude ?? place.longitude ?? place.lng ?? 0);
  const type = (place.type || (Array.isArray(place.types) ? place.types[0] : "") || place.category || "point_of_interest")
    .toString().toLowerCase().trim();
  const distanceMeters = placeLat && placeLng ? calcDistanceMeters(siteLat, siteLng, placeLat, placeLng) : 0;
  const ratingValue = typeof place.rating === "number" ? place.rating : typeof place.rating?.average === "number" ? place.rating.average : null;
  const imageUrl = (Array.isArray(place.photos) && place.photos.length > 0 ? place.photos[0] : null) || place.photoUrl || place.imageUrl || null;
  const id = place.id || place.place_id || place.placeId;
  return {
    id,
    name: place.name || "Unknown",
    address: place.address || place.vicinity || place.formatted_address || "",
    rating: ratingValue && ratingValue > 0 ? ratingValue : null,
    totalReviews: place.rating?.totalReviews ?? null,
    // openNow: place.openingStatus?.isOpen ?? place.openNow ?? place.isOpen ?? null,
    openNow: place.openingStatus?.text ?? null,
    googleMapUrl: buildMapsUrl(id, place.name, placeLat, placeLng),
    imageUrl,
    distanceMeters,
    distanceKm: distanceMeters > 0 ? (distanceMeters < 1000 ? `${Math.round(distanceMeters)} m` : `${(distanceMeters / 1000).toFixed(1)} km`) : null,
    type,
  };
};

const buildDynamicCategories = (places) => {
  const seen = new Set(), types = [];
  for (const p of places) { if (!seen.has(p.type)) { seen.add(p.type); types.push(p.type); } }
  const knownOrder = Object.keys(KNOWN_CATEGORY_MAP);
  types.sort((a, b) => {
    const ai = knownOrder.indexOf(a), bi = knownOrder.indexOf(b);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1; if (bi !== -1) return 1;
    return a.localeCompare(b);
  });
  return types.map((type) => {
    const k = KNOWN_CATEGORY_MAP[type];
    return { id: type, type, label: k?.label ?? toTitleCase(type), icon: k?.icon ?? "📌" };
  });
};

// ─── PlaceCard — plain divs, no MUI Card/CardContent/Stack overhead ───────────

const cardStyle = {
  display: "flex",
  borderRadius: 8,
  border: "1px solid #e0e0e0",
  overflow: "hidden",
  background: "#fff",
  transition: "box-shadow 0.15s",
  cursor: "default",
};

const PlaceCard = memo(({ place }) => (
  <div
    style={cardStyle}
    onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.12)"; e.currentTarget.style.borderColor = "#1976d2"; }}
    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = "#e0e0e0"; }}
  >
    {/* Thumbnail */}
    <div style={{ position: "relative", flexShrink: 0, width: 80, minHeight: 80 }}>
      <img
        src={place.imageUrl || PlaceholderImg}
        alt={place.name}
        loading="lazy"
        decoding="async"
        width={80}
        style={{ width: 80, height: "100%", minHeight: 80, objectFit: "cover", display: "block" }}
        onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = PlaceholderImg; }}
      />
      {place?.openNow && (
        <div style={{
          position: "absolute", bottom: 3, left: 3,
          background: place?.openNow === "Open now" ? "#2e7d32" : "#616161",
          color: "#fff", fontSize: 9, fontWeight: 700,
          padding: "1px 5px", borderRadius: 3, letterSpacing: 0.5, textTransform: "uppercase",
        }}>
          {place?.openNow == "Open now" ? "Open" : "Close"}
        </div>
      )}
    </div>

    {/* Content */}
    <div style={{ flex: 1, padding: "8px 12px", display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={place.name}>
        {place.name}
      </div>
      {place.address && (
        <div style={{ fontSize: 11, color: "#757575", lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {place.address}
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2, flexWrap: "wrap" }}>
        {place.distanceKm && (
          <span style={{ fontSize: 11, color: "#9e9e9e" }}>📍 {place.distanceKm}</span>
        )}
        {place.rating && (
          <span style={{ fontSize: 11, fontWeight: 600 }}>
            ⭐ {place.rating.toFixed(1)}
            {place.totalReviews > 0 && <span style={{ fontWeight: 400, color: "#9e9e9e" }}> ({place.totalReviews.toLocaleString()})</span>}
          </span>
        )}
        {place.googleMapUrl && (
          <a
            href={place.googleMapUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ marginLeft: "auto", fontSize: 11, color: "#1976d2", textDecoration: "none", fontWeight: 500 }}
          >
            Maps ↗
          </a>
        )}
      </div>
    </div>
  </div>
));
PlaceCard.displayName = "PlaceCard";

// ─── PaginatedList ────────────────────────────────────────────────────────────

const PaginatedList = memo(({ places }) => {
  const [page, setPage] = useState(1);

  // Reset to page 1 when the list identity changes
  const firstId = places[0]?.id;
  const prevFirstId = useRef(firstId);
  if (prevFirstId.current !== firstId) { prevFirstId.current = firstId; }

  const visible = places.slice(0, page * PAGE_SIZE);
  const remaining = places.length - visible.length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {visible.map((place) => <PlaceCard key={place.id} place={place} />)}
      {remaining > 0 && (
        <button
          onClick={() => setPage((p) => p + 1)}
          style={{
            alignSelf: "center", marginTop: 4, padding: "6px 20px",
            borderRadius: 8, border: "1px solid #bdbdbd", background: "#fff",
            fontSize: 12, cursor: "pointer", color: "#424242",
          }}
        >
          Load more ({remaining} remaining)
        </button>
      )}
    </div>
  );
});
PaginatedList.displayName = "PaginatedList";

// ─── CategorySection ──────────────────────────────────────────────────────────

const CategorySection = memo(({ category, places }) => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <div
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "8px 12px", borderRadius: 8, cursor: "pointer",
          background: "#f5f5f5", border: "1px solid #e0e0e0",
          userSelect: "none",
        }}
      >
        <span style={{ fontSize: 16 }}>{category.icon}</span>
        <span style={{ fontWeight: 600, fontSize: 13, flex: 1 }}>{category.label}</span>
        <span style={{
          fontSize: 11, fontWeight: 700, background: "#1976d2", color: "#fff",
          borderRadius: 10, padding: "1px 7px",
        }}>{places.length}</span>
        <span style={{ fontSize: 11, color: "#9e9e9e" }}>{open ? "▼" : "▶"}</span>
      </div>
      {open && (
        <div style={{ marginTop: 6, marginBottom: 6 }}>
          <PaginatedList places={places} />
        </div>
      )}
    </div>
  );
});
CategorySection.displayName = "CategorySection";

// ─── LoadingSkeleton ──────────────────────────────────────────────────────────

const LoadingSkeleton = () => (
  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
    {[...Array(4)].map((_, i) => (
      <div key={i} style={{ display: "flex", gap: 12, padding: 12, borderRadius: 8, border: "1px solid #e0e0e0" }}>
        <Skeleton variant="rectangular" width={80} height={70} sx={{ borderRadius: 1, flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <Skeleton width="55%" height={14} />
          <Skeleton width="75%" height={12} sx={{ mt: 0.5 }} />
          <Skeleton width="35%" height={12} sx={{ mt: 0.5 }} />
        </div>
      </div>
    ))}
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const SitesServicesSection = ({ siteInfo, activeTab }) => {
  const [allPlaces, setAllPlaces]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError]           = useState("");
  const [loading, setLoading]       = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [inputValue, setInputValue] = useState("");
  const [activeApiValue, setActiveApiValue] = useState("all"); // what actually goes to backend

  const { loginData } = useSelector((state) => state?.main);
  const siteLat = useMemo(() => Number(siteInfo?.latitude ?? 0), [siteInfo?.latitude]);
  const siteLng = useMemo(() => Number(siteInfo?.longitude ?? 0), [siteInfo?.longitude]);
  const hasLocation = !Number.isNaN(siteLat) && !Number.isNaN(siteLng) && (siteLat !== 0 || siteLng !== 0);

  // ── Debounce: input → apiValue ────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => {
      setActiveApiValue(resolveSearchToApiValue(inputValue));
      setSelectedCategory("all");
    }, 500);
    return () => clearTimeout(t);
  }, [inputValue]);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (activeTab !== "services") return;
    if (!hasLocation) { setAllPlaces([]); setCategories([]); setError(""); setLoading(false); return; }

    setLoading(true);
    setError("");
    setAllPlaces([]);
    setCategories([]);

    let cancelled = false;
    (async () => {
      try {
        const response = await Postrequestcall(
          GET_NEARBY_PLACES,
          { Latitude: siteLat.toString(), Longitude: siteLng.toString(), type: activeApiValue },
          loginData?.accessToken
        );
        if (cancelled) return;
        if (response?.status !== 200) throw new Error("Non-200");
        const raw = response?.data?.data?.results ?? [];
        const normalized = raw
          .map((p) => normalizePlace(p, siteLat, siteLng))
          .sort((a, b) => a.distanceMeters - b.distanceMeters);
        setAllPlaces(normalized);
        setCategories(buildDynamicCategories(normalized));
      } catch {
        if (!cancelled) setError("Unable to load nearby services. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, hasLocation, siteLat, siteLng, loginData?.accessToken, activeApiValue]);

  // ── Derived ───────────────────────────────────────────────────────────────

  const placesForCategory = useMemo(
    () => selectedCategory === "all" ? allPlaces : allPlaces.filter((p) => p.type === selectedCategory),
    [allPlaces, selectedCategory]
  );

  const categoriesWithCounts = useMemo(
    () => categories.map((cat) => ({ ...cat, count: allPlaces.filter((p) => p.type === cat.type).length })),
    [categories, allPlaces]
  );

  const groupedForAll = useMemo(() => {
    if (selectedCategory !== "all") return null;
    return categoriesWithCounts
      .filter((cat) => cat.count > 0)
      .map((cat) => ({ ...cat, places: allPlaces.filter((p) => p.type === cat.type) }));
  }, [selectedCategory, categoriesWithCounts, allPlaces]);

  const handleCategoryClick = useCallback((id) => setSelectedCategory(id), []);

  // ── Guards ────────────────────────────────────────────────────────────────

  if (!hasLocation) {
    return (
      <div style={{ paddingTop: 16 }}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Add a location to this site to see nearby services.
        </Typography>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingTop: 4 }}>
      {error && <Typography variant="body2" sx={{ color: "error" }}>{error}</Typography>}

      {/* Search */}
      {/* <TextField
        size="small"
        placeholder="Search by category (e.g. grocery, cafe, hospital…)"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
            </InputAdornment>
          ),
          endAdornment: loading && inputValue ? (
            <InputAdornment position="end">
              <Typography variant="caption"  sx={{color: "text.disabled",  fontSize: "0.65rem" }}>searching…</Typography>
            </InputAdornment>
          ) : null,
        }}
        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: "0.82rem" } }}
      /> */}

      {/* Category chips */}
      {!loading && categories.length > 0 && (
        <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4 }}>
          <Chip
            label={`All (${allPlaces.length})`} size="small" clickable
            color={selectedCategory === "all" ? "primary" : "default"}
            onClick={() => handleCategoryClick("all")}
            sx={{ fontWeight: selectedCategory === "all" ? 700 : 400, fontSize: "0.7rem", flexShrink: 0 }}
          />
          {categoriesWithCounts.map((cat) => (
            <Chip
              key={cat.id}
              label={`${cat.icon} ${cat.label} (${cat.count})`}
              size="small" clickable
              color={selectedCategory === cat.id ? "primary" : "default"}
              onClick={() => handleCategoryClick(cat.id)}
              sx={{ fontWeight: selectedCategory === cat.id ? 700 : 400, fontSize: "0.7rem", flexShrink: 0 }}
            />
          ))}
        </div>
      )}

      {/* Loading */}
      {loading && <LoadingSkeleton />}

      {/* Single empty state */}
      {!loading && allPlaces.length === 0 && (
        <Typography variant="body2"  sx={{color: "text.secondary",  fontStyle: "italic" }}>
          {inputValue.trim() ? `No places found for "${inputValue.trim()}".` : "No nearby places found for this location."}
        </Typography>
      )}

      {/* Content */}
      {!loading && allPlaces.length > 0 && (
        selectedCategory === "all" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {groupedForAll?.map((cat) => (
              <CategorySection key={cat.id} category={cat} places={cat.places} />
            ))}
          </div>
        ) : (
          <PaginatedList places={placesForCategory} />
        )
      )}
    </div>
  );
};

export default SitesServicesSection;